import type { BioContent, User } from "@fitties/shared";
import { calculateAge } from "@shared/utils/ageUtils";
import { Search, User as UserIcon } from "lucide-react";
import { BioMessageChain } from "./BioMessageChain";
import { ProfileAttributePill } from "./ProfileAttributePill";

type GenderKey = "female" | "male" | "nonbinary";

const GENDER_LABELS: Record<GenderKey, string> = {
  female: "Female",
  male: "Male",
  nonbinary: "Other",
};

const DESIRED_GENDER_LABELS: Record<GenderKey, string> = {
  female: "Women",
  male: "Men",
  nonbinary: "Other",
};

function getInterestedInLabel(desiredGenders: GenderKey[]): string {
  if (desiredGenders.length === 3) {
    return "Everyone";
  }
  return desiredGenders.map((g) => DESIRED_GENDER_LABELS[g]).join(", ");
}

/**
 * Minimal user shape for profile card display (allows partial data from APIs).
 */
export interface ProfileCardUser {
  display_name: string;
  bio?: string | null | undefined;
  bioContent?: BioContent | null | undefined;
  photo_url?: string | null;
  date_of_birth?: Date | string;
  age?: number;
  gender?: GenderKey;
  desired_genders?: GenderKey[];
}

interface ProfileCardViewProps {
  /** User data to display */
  user: ProfileCardUser | User;
}

function resolveAge(user: ProfileCardUser | User): number | null {
  if ("age" in user && typeof user.age === "number" && Number.isFinite(user.age)) {
    return user.age;
  }
  const dob = "date_of_birth" in user ? user.date_of_birth : undefined;
  if (!dob) {
    return null;
  }
  const date = dob instanceof Date ? dob : new Date(dob);
  return calculateAge(date);
}

/**
 * Read-only profile card view: full-bleed photo, gradient, name, age, bio.
 * No swipe or like/reject. Used by profile preview and match profile pages.
 */
function hasPills(user: ProfileCardUser | User): boolean {
  if (user.gender) {
    return true;
  }
  const dg = user.desired_genders;
  return Array.isArray(dg) && dg.length > 0;
}

export function ProfileCardView({ user }: ProfileCardViewProps) {
  const age = resolveAge(user);
  const displayName = user.display_name ?? "Unknown";
  const genderLabel = user.gender ? GENDER_LABELS[user.gender] : null;
  const desiredGenders = user.desired_genders ?? [];
  const interestedInLabel = desiredGenders.length > 0 ? getInterestedInLabel(desiredGenders) : null;
  const showPills = hasPills(user);

  return (
    <div className="relative h-full w-full" data-testid="profile-card-view">
      <div className="relative h-full w-full">
        {user.photo_url ? (
          <img
            src={user.photo_url}
            alt={`${displayName}'s profile`}
            className="h-full w-full object-cover"
            data-testid="profile-card-photo"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-300" data-testid="profile-card-photo">
            <span className="text-lg text-gray-600">No photo</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gray-100 p-6" data-testid="profile-card-user-info">
        <div className="mb-4">
          <h2 className="mb-2 text-4xl font-bold text-gray-900" style={{ fontFamily: "Courier Prime, monospace" }}>
            {displayName}
            {age != null && <span className="ml-3 text-3xl font-normal text-gray-600">{age}</span>}
          </h2>
        </div>
        <BioMessageChain className="mb-6 text-lg leading-relaxed" compact bio={user.bio} bioContent={user.bioContent} />
        {showPills && (
          <div className="flex flex-wrap gap-2" data-testid="profile-card-pills">
            {genderLabel && (
              <ProfileAttributePill
                icon={<UserIcon className="size-4" />}
                label={genderLabel}
                variant="gender"
                data-testid="profile-card-pill-gender"
              />
            )}
            {interestedInLabel && (
              <ProfileAttributePill
                icon={<Search className="size-4" />}
                label={interestedInLabel}
                variant="interestedIn"
                data-testid="profile-card-pill-interested-in"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
